<script setup lang="ts">
import { yieldToMain } from '@@/bits/global'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import type { Id } from '@@/types'
import SurfaceHeader from '@@/vuecomponents/SurfaceHeader.vue'
import SurfacePostsGridMasonry from '@@/vuecomponents/SurfacePostsGridMasonry.vue'
import { storeToRefs } from 'pinia'
import { onMounted, ref } from 'vue'

withDefaults(
  defineProps<{
    reactionIdsByPostId: Record<Id, Id[]>
    commentIdsByPostId: Record<Id, Id[]>
  }>(),
  {
    reactionIdsByPostId: () => ({}),
    commentIdsByPostId: () => ({}),
  },
)

defineEmits(['scroll'])

const { xHeader, backgroundLuminance, isReadOnly, isScreenshotMode } = storeToRefs(useSurfaceStore())
const { allSortedPosts } = storeToRefs(useSurfacePostsStore())

const wallContainer = ref<HTMLDivElement>()

const isReadyToMountSurfacePostsGridMasonry = ref(false)
const isReadyToMountSurfaceHeader = ref(false)
onMounted(async () => {
  // We yield execution back to the browser before mounting the internal components to avoid blocking the main thread.
  // This is to avoid high TBT (Total Blocking Time), which contributes negatively to the Core Web Vitals.
  await yieldToMain()
  isReadyToMountSurfacePostsGridMasonry.value = true
  await yieldToMain()
  isReadyToMountSurfaceHeader.value = true
})
</script>

<template>
  <div
    id="wall-container"
    ref="wallContainer"
    :class="[
      !isReadOnly && 'overflow-x-hidden',
      !isScreenshotMode && 'scrollbar-overlay',
      {
        'scrollbar-photo': backgroundLuminance === 'light',
        'scrollbar-photo-dark': backgroundLuminance === 'dark',
        'scrollbar-gutter-stable': true,
      },
    ]"
    @scroll="$emit('scroll', $event)"
  >
    <SurfaceHeader v-if="xHeader && isReadyToMountSurfaceHeader" />
    <SurfacePostsGridMasonry
      v-if="wallContainer && isReadyToMountSurfacePostsGridMasonry"
      :reaction-ids-by-post-id="reactionIdsByPostId"
      :comment-ids-by-post-id="commentIdsByPostId"
      :posts="allSortedPosts"
      :wall-container="wallContainer"
    />
  </div>
</template>
